import type { FileType } from '../components/Editor';

export interface ImportedFile {
  name: string;
  content: string;
  type: string;
  fileType: FileType;
}

// Map file extensions to FileType
const extensionToFileType: Record<string, FileType> = {
  '.md': 'markdown',
  '.markdown': 'markdown',
  '.txt': 'text',
  '.json': 'json',
  '.js': 'javascript',
  '.jsx': 'javascript',
  '.ts': 'typescript',
  '.tsx': 'typescript',
  '.php': 'php',
  '.py': 'python'
};

class FileImportService {
  private dropZone: HTMLElement | null = null;

  // Check if File System Access API is available
  hasFileSystemAccess(): boolean {
    return 'showOpenFilePicker' in window;
  }

  // Initialize drag & drop zone
  initDropZone(callback: (files: ImportedFile[]) => void): void {
    // Create drop zone overlay
    this.dropZone = document.createElement('div');
    this.dropZone.className = 'drop-zone hidden';
    this.dropZone.textContent = 'Suelta el archivo aquí';
    document.body.appendChild(this.dropZone);

    let dragCounter = 0;

    document.addEventListener('dragenter', (e) => {
      e.preventDefault();
      dragCounter++;
      this.dropZone?.classList.remove('hidden');
    });

    document.addEventListener('dragleave', (e) => {
      e.preventDefault();
      dragCounter--;
      if (dragCounter === 0) {
        this.dropZone?.classList.add('hidden');
      }
    });

    document.addEventListener('dragover', (e) => {
      e.preventDefault();
    });

    document.addEventListener('drop', async (e) => {
      e.preventDefault();
      dragCounter = 0;
      this.dropZone?.classList.add('hidden');

      const files = await this.processDroppedFiles(e.dataTransfer?.files);
      if (files.length > 0) {
        callback(files);
      }
    });
  }

  private async processDroppedFiles(fileList: FileList | undefined): Promise<ImportedFile[]> {
    if (!fileList) return [];

    const files: ImportedFile[] = [];
    const allowedExtensions = Object.keys(extensionToFileType);

    for (const file of Array.from(fileList)) {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();

      if (allowedExtensions.includes(ext)) {
        const content = await this.readFile(file);
        files.push({
          name: file.name.replace(/\.[^.]+$/, ''), // Remove extension
          content,
          type: ext,
          fileType: extensionToFileType[ext] || 'text'
        });
      }
    }

    return files;
  }

  private readFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }

  // Open file using File System Access API
  async openFile(): Promise<ImportedFile | null> {
    if (this.hasFileSystemAccess()) {
      return this.openFileWithFSA();
    } else {
      return this.openFileWithInput();
    }
  }

  private async openFileWithFSA(): Promise<ImportedFile | null> {
    try {
      const [handle] = await (window as any).showOpenFilePicker({
        types: [
          {
            description: 'Text files',
            accept: {
              'text/markdown': ['.md', '.markdown'],
              'text/plain': ['.txt'],
              'application/json': ['.json'],
              'text/javascript': ['.js', '.jsx'],
              'text/typescript': ['.ts', '.tsx'],
              'text/x-php': ['.php'],
              'text/x-python': ['.py']
            }
          }
        ],
        multiple: false
      });

      const file = await handle.getFile();
      const content = await file.text();
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();

      return {
        name: file.name.replace(/\.[^.]+$/, ''),
        content,
        type: ext,
        fileType: extensionToFileType[ext] || 'text'
      };
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Error opening file:', error);
      }
      return null;
    }
  }

  private openFileWithInput(): Promise<ImportedFile | null> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.md,.markdown,.txt,.json,.js,.jsx,.ts,.tsx,.php,.py';

      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) {
          resolve(null);
          return;
        }

        const content = await this.readFile(file);
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        resolve({
          name: file.name.replace(/\.[^.]+$/, ''),
          content,
          type: ext,
          fileType: extensionToFileType[ext] || 'text'
        });
      };

      input.oncancel = () => resolve(null);
      input.click();
    });
  }

  // Open folder using File System Access API
  async openFolder(): Promise<ImportedFile[]> {
    if (!this.hasFileSystemAccess()) {
      console.warn('File System Access API not available');
      return [];
    }

    try {
      const dirHandle = await (window as any).showDirectoryPicker();
      const files: ImportedFile[] = [];
      const allowedExtensions = Object.keys(extensionToFileType);

      for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file') {
          const ext = '.' + entry.name.split('.').pop()?.toLowerCase();
          if (allowedExtensions.includes(ext)) {
            const file = await entry.getFile();
            const content = await file.text();
            files.push({
              name: entry.name.replace(/\.[^.]+$/, ''),
              content,
              type: ext,
              fileType: extensionToFileType[ext] || 'text'
            });
          }
        }
      }

      return files;
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Error opening folder:', error);
      }
      return [];
    }
  }
}

export const fileImportService = new FileImportService();
